<template>
  <div class="collection-settings">
    <!-- Loading skeleton -->
    <div v-if="loading" class="collection-skel">
      <div class="skel-row"></div>
      <div class="skel-row skel-row--sm"></div>
    </div>

    <!-- Error de conexión con backend -->
    <div v-else-if="backendUnavailable" class="cs-unavailable">
      <i class="el-icon-warning-outline cs-unavailable__icon"></i>
      <div class="cs-unavailable__text">
        <span class="cs-unavailable__title">Configuración no disponible</span>
        <span class="cs-unavailable__desc">
          No se pudo conectar con el servidor. Intenta recargar la página.
        </span>
      </div>
      <el-button size="mini" @click="loadSettings">Reintentar</el-button>
    </div>

    <template v-else>
      <!-- Bloque 1: Seguimiento de cobros -->
      <div class="cs-block">
        <div class="cs-block__header">
          <div class="cs-block__info">
            <span class="cs-block__title"
              >Seguimiento de cobros en dashboard</span
            >
            <span class="cs-block__desc">
              Activa para ver en el dashboard el total de cobros pendientes y
              facturas vencidas. Recomendado solo si registras los pagos en
              Mydessk.
            </span>
          </div>
          <el-switch
            v-model="form.collection_tracking_enabled"
            :disabled="saving"
            active-color="#3ecfb4"
            @change="onTrackingToggle"
          />
        </div>

        <!-- Fecha de inicio (solo visible si tracking activo) -->
        <transition name="cs-fade">
          <div v-if="form.collection_tracking_enabled" class="cs-start-date">
            <label class="cs-label">
              Rastrear facturas desde
              <el-tooltip
                content="Solo se contabilizarán facturas creadas a partir de esta fecha. Facturas anteriores no afectan las métricas."
                placement="top"
                effect="dark"
              >
                <i class="el-icon-info cs-info-icon"></i>
              </el-tooltip>
            </label>
            <el-date-picker
              v-model="form.collection_start_date"
              type="date"
              placeholder="Desde el inicio (todas las facturas)"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              :picker-options="datePickerOptions"
              :disabled="saving"
              class="cs-datepicker"
              clearable
              @change="saveSettings"
            />
            <span class="cs-label-hint">
              Deja en blanco para incluir todas las facturas históricas.
            </span>
          </div>
        </transition>
      </div>

      <div class="cs-divider"></div>

      <!-- Bloque 2: Recordatorios automáticos -->
      <div
        class="cs-block"
        :class="{ 'cs-block--disabled': !form.collection_tracking_enabled }"
      >
        <div class="cs-block__header">
          <div class="cs-block__info">
            <span class="cs-block__title">
              Recordatorios automáticos de pago
              <span
                v-if="!form.collection_tracking_enabled"
                class="cs-badge-requires"
              >
                Requiere seguimiento activo
              </span>
            </span>
            <span class="cs-block__desc">
              Envía recordatorios automáticos por email o WhatsApp a clientes
              con facturas pendientes. Puedes configurar la frecuencia por cada
              factura.
            </span>
          </div>
          <el-switch
            v-model="form.reminders_active"
            :disabled="saving || !form.collection_tracking_enabled"
            active-color="#3ecfb4"
            @change="saveSettings"
          />
        </div>
      </div>

      <!-- Bloque 3: Plantilla de WhatsApp para cobros -->
      <transition name="cs-fade">
        <div v-if="form.reminders_active" class="cs-divider"></div>
      </transition>
      <transition name="cs-fade">
        <div v-if="form.reminders_active" class="cs-block cs-wa-block">
          <div class="cs-block__info">
            <span class="cs-block__title"
              >Plantilla de WhatsApp para cobros</span
            >
            <span class="cs-block__desc">
              Elige la plantilla aprobada que se usará al enviar los
              recordatorios de facturas pendientes por WhatsApp. Sin plantilla,
              los recordatorios se envían solo por email.
            </span>
          </div>
          <div v-if="loadingWaTemplate" class="cs-wa-loading">
            <i class="el-icon-loading"></i> Cargando...
          </div>
          <template v-else>
            <div class="cs-wa-status">
              <template v-if="waTemplateValue">
                <i class="el-icon-success cs-wa-status__on"></i>
                Activa: <strong>{{ waTemplateValue.templateName }}</strong>
                <a
                  href="#"
                  class="cs-wa-status__remove"
                  @click.prevent="removeWaTemplate"
                  >Quitar</a
                >
              </template>
              <template v-else>
                <i class="el-icon-info"></i>
                Aún no hay plantilla activa: los recordatorios se envían solo
                por email. Elige una abajo y pulsa
                <strong>Usar esta plantilla para cobros</strong> para activarla.
              </template>
            </div>
            <!-- El picker preselecciona y emite una plantilla al montarse: la
                 selección queda como borrador y solo se guarda con el botón. -->
            <WhatsAppTemplatePicker
              :value="waTemplateValue"
              module-type="invoice"
              source="marketing"
              @input="waTemplateDraft = $event"
            />
            <div v-if="waTemplateDirty" class="cs-wa-actions">
              <el-button
                type="primary"
                size="small"
                :loading="savingWaTemplate"
                @click="saveWaTemplate"
                >Usar esta plantilla para cobros</el-button
              >
            </div>
          </template>
        </div>
      </transition>

      <!-- Mensaje de estado -->
      <transition name="cs-fade">
        <div v-if="saved" class="cs-saved">
          <i class="el-icon-check"></i> Configuración guardada
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import configService from "../../services/configService";
import reminderService from "@/modules/dashboard/reminders/services/ReminderService";
import WhatsAppTemplatePicker from "@/modules/dashboard/components/WhatsAppTemplatePicker";

export default {
  name: "CollectionSettings",
  components: { WhatsAppTemplatePicker },
  data() {
    return {
      loading: true,
      saving: false,
      saved: false,
      backendUnavailable: false,
      savedTimer: null,
      form: {
        collection_tracking_enabled: false,
        collection_start_date: null,
        reminders_active: false
      },
      waTemplateValue: null,
      waTemplateDraft: null,
      loadingWaTemplate: false,
      savingWaTemplate: false,
      datePickerOptions: {
        disabledDate(date) {
          return date > new Date();
        }
      }
    };
  },
  computed: {
    waTemplateDirty() {
      const d = this.waTemplateDraft;
      if (!d || !d.templateId) return false;
      const s = this.waTemplateValue;
      if (!s) return true;
      return (
        d.templateId !== s.templateId ||
        JSON.stringify(d.variables || []) !== JSON.stringify(s.variables || [])
      );
    }
  },
  watch: {
    "form.reminders_active"(val) {
      if (val) this.loadWaTemplate();
    }
  },
  async mounted() {
    await this.loadSettings();
  },
  beforeDestroy() {
    if (this.savedTimer) clearTimeout(this.savedTimer);
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      this.backendUnavailable = false;
      try {
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const res = await configService.getCollectionConfig(businessId);
        if (res.data && res.data.success) {
          const d = res.data.data;
          this.form.collection_tracking_enabled = !!d.collection_tracking_enabled;
          this.form.collection_start_date = d.collection_start_date || null;
          this.form.reminders_active = !!d.reminders_active;
          if (this.form.reminders_active) await this.loadWaTemplate();
        } else {
          this.backendUnavailable = true;
        }
      } catch {
        // Endpoint no disponible — mostrar estado de no disponible
        this.backendUnavailable = true;
      } finally {
        this.loading = false;
      }
    },

    async loadWaTemplate() {
      this.loadingWaTemplate = true;
      this.waTemplateDraft = null;
      try {
        const res = await reminderService.getCollectionWhatsappTemplate();
        const d = res.data && res.data.success ? res.data.data : null;
        // Plantilla de marketing: id = "nombre::idioma" (mismo formato que el picker)
        this.waTemplateValue = d
          ? {
              templateId: d.whatsapp_template_name
                ? `${d.whatsapp_template_name}::${d.whatsapp_template_language}`
                : d.whatsapp_template_id,
              templateName:
                d.whatsapp_template_name || (d.template ? d.template.name : ""),
              variables: d.whatsapp_template_variables || []
            }
          : null;
      } catch (e) {
        this.waTemplateValue = null;
      } finally {
        this.loadingWaTemplate = false;
      }
    },

    async saveWaTemplate() {
      await this.persistWaTemplate(this.waTemplateDraft);
    },

    async removeWaTemplate() {
      await this.persistWaTemplate(null);
    },

    async persistWaTemplate(val) {
      this.savingWaTemplate = true;
      try {
        const res = await reminderService.setCollectionWhatsappTemplate({
          whatsapp_template_name: val ? val.templateName : null,
          whatsapp_template_language: val ? val.templateLanguage : null,
          whatsapp_template_variables: val ? val.variables : null
        });
        if (res.data && res.data.success === false) {
          throw new Error(res.data.message);
        }
        this.waTemplateValue = val
          ? {
              templateId: val.templateId,
              templateName: val.templateName,
              variables: val.variables || []
            }
          : null;
        this.showSaved();
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "No se pudo guardar la plantilla de cobro";
        this.$notifications.error(msg);
      } finally {
        this.savingWaTemplate = false;
      }
    },

    onTrackingToggle(val) {
      if (!val) {
        this.form.reminders_active = false;
        this.form.collection_start_date = null;
      }
      this.saveSettings();
    },

    async saveSettings() {
      if (this.saving) return;
      this.saving = true;
      this.saved = false;
      try {
        const payload = {
          collection_tracking_enabled: this.form.collection_tracking_enabled,
          collection_start_date: this.form.collection_start_date || null,
          reminders_active: this.form.reminders_active
        };
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const res = await configService.updateCollectionConfig(
          payload,
          businessId
        );
        if (res.data && res.data.success) {
          const d = res.data.data;
          this.form.collection_tracking_enabled = !!d.collection_tracking_enabled;
          this.form.collection_start_date = d.collection_start_date || null;
          this.form.reminders_active = !!d.reminders_active;
          this.showSaved();
          this.$root.$emit("dashboard-kpis-refresh");
        }
      } catch (e) {
        this.$notifications.error("No se pudo guardar la configuración");
      } finally {
        this.saving = false;
      }
    },

    showSaved() {
      this.saved = true;
      if (this.savedTimer) clearTimeout(this.savedTimer);
      this.savedTimer = setTimeout(() => {
        this.saved = false;
      }, 2500);
    }
  }
};
</script>

<style lang="scss" scoped>
.collection-settings {
  padding: 4px 0;
}

.collection-skel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.skel-row {
  height: 56px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e4e8f1 50%, #f0f2f7 75%);
  background-size: 800px 100%;
  animation: cs-shimmer 1.4s infinite linear;
  &--sm {
    height: 36px;
  }
}
@keyframes cs-shimmer {
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
}

// Estado: backend no disponible
.cs-unavailable {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  background: #fafbfc;
  border: 1.5px dashed #e4eaf6;
  border-radius: 10px;

  &__icon {
    font-size: 22px;
    color: #e6a23c;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 1;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: #606266;
  }

  &__desc {
    font-size: 12px;
    color: #909399;
  }
}

.cs-block {
  padding: 18px 0;
  transition: opacity 0.2s;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #31355c;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__desc {
    font-size: 13px;
    color: #909399;
    line-height: 1.5;
  }
}

.cs-badge-requires {
  font-size: 11px;
  font-weight: 500;
  background: #f0f2f7;
  color: #909399;
  border-radius: 6px;
  padding: 2px 8px;
}

.cs-divider {
  height: 1px;
  background: #f0f2f7;
}

.cs-start-date {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cs-label {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 5px;
}

.cs-info-icon {
  font-size: 13px;
  color: #c0c4cc;
  cursor: help;
  &:hover {
    color: #909399;
  }
}

.cs-datepicker {
  width: 100%;
  max-width: 260px;
}

.cs-label-hint {
  font-size: 12px;
  color: #c0c4cc;
}

.cs-saved {
  margin-top: 12px;
  font-size: 13px;
  color: #1d9e75;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.cs-wa-block {
  padding-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cs-wa-status {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;

  i {
    margin-right: 4px;
  }
}

.cs-wa-status__on {
  color: #1d9e75;
}

.cs-wa-status__remove {
  margin-left: 4px;
  color: #f56c6c;
  font-size: 12px;
}

.cs-wa-actions {
  display: flex;
  justify-content: flex-end;
}

.cs-wa-loading {
  font-size: 13px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 6px;
}

.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
